import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShiftsTimeline } from '@/components/Shifts/ShiftsTimeline'
import { requirePermission } from '@/utils/auth'
import { CenteredLoader } from '@/components/CenteredLoader'
import { Alert, Text, Button, Group, Accordion, Box, Card, Select, Modal, Title } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useShiftsData } from '@/hooks/useShiftsData'
import type { useNavigate as useNavigateType } from '@tanstack/react-router'
import type { UserIntrospectionResponse } from '@/api/main/types.gen'

type DayShifts = ReturnType<typeof useShiftsData>['dayShifts']
type NavigateFn = ReturnType<typeof useNavigateType>
type UserPermissions = UserIntrospectionResponse | null
import { TbCalendarTime } from 'react-icons/tb'
import { DateRangePicker } from '@/components/DateRangePicker'
import { IconRefresh } from '@tabler/icons-react'
import { formatDateForUrl } from '@/utils/datetime'
import { useDisclosure, useLocalStorage } from '@mantine/hooks'
import { useState } from 'react'
import PageShell from '@/components/Common/PageShell'
import { ConfigureView } from '@/components/ConfigureView'
import { parseAndValidateDates } from '@/utils/datetime'
import { TableColumn } from '@/components/Table/GenericTable'
import { PermissionGuard } from '@/utils/permissions'
import { useAuthStore } from '@/stores/auth'
import { useTranslation } from 'react-i18next'

// Configure dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)

export const Route = createFileRoute('/shifts/$startDate/$endDate')({
  beforeLoad: () => requirePermission('shifts'),
  component: ShiftsPage,
  parseParams: params => ({
    startDate: params.startDate,
    endDate: params.endDate,
  }),
})

const ROUTE_PATH = '/shifts/$startDate/$endDate'

type Channels = TableColumn<{
  label: string
  value: string
  key: string
  visible: boolean
  sortable: boolean
}>[]

function ShiftsContent({
  dayShifts,
  startDate,
  endDate,
  navigate,
  syncShifts,
  syncShiftsOnDemandLoading,
  handleDeleteShift,
  userPermissions,
  filteredChannel,
  setFilteredChannel,
  isMobile,
  error,
  syncShiftsOnDemandQueryError,
}: {
  dayShifts: DayShifts
  startDate: Date
  endDate: Date
  navigate: NavigateFn
  syncShifts: () => void
  syncShiftsOnDemandLoading: boolean
  handleDeleteShift: (id: number) => void
  userPermissions: UserPermissions
  filteredChannel: string | null
  setFilteredChannel: (channel: string | null) => void
  isMobile: boolean
  error: Error | null
  syncShiftsOnDemandQueryError: string | null
}) {
  const { t } = useTranslation('shifts')
  const [opened, { open, close }] = useDisclosure(false)
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null)

  const openDeleteModal = (shiftId: string) => {
    setDeleteCandidate(shiftId)
    open()
  }

  const [accordionValue, setAccordionValue] = useLocalStorage<string | null>({
    key: 'dateRangePickerAccordionOpen',
    defaultValue: null,
    getInitialValueInEffect: false,
  })

  const [channels, setChannels] = useLocalStorage<Channels>({
    key: 'shifts-channels',
    defaultValue: Array.from(new Set(dayShifts.flatMap(day => day.vehicleShifts.map(shift => shift.vehicle_id))))
      .sort()
      .map(c => ({
        key: c,
        label: c,
        visible: true,
        sortable: true,
        value: c,
      })) as Channels,
  })

  const navigateToDateRange = (newStartDate: Date, newEndDate: Date) => {
    navigate({
      to: ROUTE_PATH,
      params: {
        startDate: formatDateForUrl(newStartDate),
        endDate: formatDateForUrl(newEndDate),
      },
    })
  }

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    if (start && end) {
      navigateToDateRange(start, end)
    }
  }

  const actions = (
    <Group>
      <PermissionGuard userPermissions={userPermissions} resource="shifts" action="write">
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={() => syncShifts()}
          loading={syncShiftsOnDemandLoading}
          variant="light"
          disabled={syncShiftsOnDemandLoading}
        >
          {t('syncShifts')}
        </Button>
      </PermissionGuard>
      <ConfigureView columns={channels} onColumnsChange={setChannels} title={t('configureView', { ns: 'common' })} />
    </Group>
  )

  const visibleChannels = channels.filter(channel => channel.visible)

  return (
    <PageShell
      title={
        <Title order={2} mb={0} c="light-dark(#868e96, #c9c9c9)">
          {t('shifts')}
        </Title>
      }
      fullWidth
      action={actions}
    >
      {(error || syncShiftsOnDemandQueryError) && (
        <Alert title={t('errorLoadingShifts')} color="red" variant="filled" style={{ margin: '20px' }} bg="red.5">
          {syncShiftsOnDemandQueryError && <Text mb="md">{syncShiftsOnDemandQueryError}</Text>}
          <Text mb="md">{t('failedToLoadShiftsData')}</Text>
          <Group></Group>
        </Alert>
      )}

      <Card
        shadow="md"
        pr="xl"
        pl="xl"
        pb="xs"
        pt="xs"
        radius="md"
        withBorder
        style={{
          position: 'sticky',
          top: 59,
          zIndex: 100,
        }}
      >
        {isMobile && (
          <Group justify="space-between" w="100%">
            <Select
              label={t('selectVehicle')}
              placeholder={t('pickVehicle')}
              data={visibleChannels.map(channel => ({
                label: channel.label,
                value: channel.key,
              }))}
              defaultValue={channels[0]?.key}
              onChange={value => {
                if (value) {
                  setFilteredChannel(value)
                }
              }}
              clearable
              styles={{
                root: {
                  width: '100%',
                },
              }}
            />
          </Group>
        )}

        <Accordion radius="md" value={accordionValue} onChange={setAccordionValue}>
          <Accordion.Item value="dateRange">
            <Accordion.Control icon={<TbCalendarTime size={22} color="var(--mantine-color-dimmed)" />}>
              {dayjs(startDate).format('DD.MM.YYYY')} - {dayjs(endDate).format('DD.MM.YYYY')}
            </Accordion.Control>
            <Accordion.Panel>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(date: Date | null) => {
                  if (date) {
                    navigateToDateRange(date, endDate)
                  }
                }}
                onEndDateChange={(date: Date | null) => {
                  if (date) {
                    navigateToDateRange(startDate, date)
                  }
                }}
                onRangeChange={handleDateRangeChange}
                mb="xl"
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        {!filteredChannel && (
          <Box
            style={{
              position: 'relative',
              height: 60,
              marginLeft: 80, // Align with time axis
            }}
          >
            {visibleChannels.map((channel, channelIndex) => {
              const channelWidth = 100 / visibleChannels.length
              const channelLeft = channelIndex * channelWidth + channelWidth * 0.05
              const channelRight = (channelIndex + 1) * channelWidth - channelWidth * 0.05

              return (
                <Box
                  key={channel.key}
                  style={{
                    position: 'absolute',
                    left: `${channelLeft}%`,
                    right: `${100 - channelRight}%`,
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'wrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {channel.label}
                </Box>
              )
            })}
          </Box>
        )}
      </Card>
      {dayShifts.map(({ vehicleShifts, allChannels, timelineStart }, index) => (
        <ShiftsTimeline
          key={index}
          vehicleShifts={
            filteredChannel ? vehicleShifts.filter(shift => shift.vehicle_id === filteredChannel) : vehicleShifts
          }
          allChannels={
            filteredChannel
              ? allChannels.filter(channel => channel.includes(filteredChannel))
              : visibleChannels.map(channel => channel.key)
          }
          onDeleteShift={openDeleteModal}
          isDeleting={false}
          timelineStart={timelineStart}
          isContinuation={index > 0}
          isEditable
        />
      ))}

      <Modal opened={opened} onClose={close} title={t('deleteShift')} centered>
        <Text mb="lg">{t('confirmDeleteShift', { shiftId: deleteCandidate })}</Text>
        <Group justify="space-between">
          <Button onClick={close} color="gray.6">
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button
            onClick={() => {
              handleDeleteShift(Number(deleteCandidate))
              close()
            }}
            color="red.6"
          >
            {t('delete', { ns: 'common' })}
          </Button>
        </Group>
      </Modal>
    </PageShell>
  )
}

function ShiftsPage() {
  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const navigate = useNavigate()
  const { userPermissions } = useAuthStore()

  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  const {
    isLoading,
    error,
    dayShifts,
    syncShifts,
    syncShiftsOnDemandQueryError,
    syncShiftsOnDemandLoading,
    handleDeleteShift,
    filteredChannel,
    setFilteredChannel,
    isMobile,
  } = useShiftsData(startDate, endDate)

  if (isLoading) return <CenteredLoader />

  return (
    <ShiftsContent
      dayShifts={dayShifts}
      startDate={startDate}
      endDate={endDate}
      navigate={navigate}
      syncShifts={syncShifts}
      syncShiftsOnDemandLoading={syncShiftsOnDemandLoading}
      handleDeleteShift={handleDeleteShift}
      userPermissions={userPermissions}
      filteredChannel={filteredChannel}
      setFilteredChannel={setFilteredChannel}
      isMobile={isMobile}
      error={error}
      syncShiftsOnDemandQueryError={syncShiftsOnDemandQueryError}
    />
  )
}

export default ShiftsPage
